import React, { useCallback } from "react";
import { connect } from "react-redux";

import Divider from "antd/es/divider";
import Switch from "antd/es/switch";
import Tag from "antd/es/tag";
import { FormattedMessage, useIntl } from "react-intl";

import { EntityTypesSupportedByIntegrations } from "@mapmycustomers/shared/types/entity";

import ButtonLink from "@app/component/ButtonLink";
import Checkbox from "@app/component/input/Checkbox";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import localSettings from "@app/config/LocalSettings";
import { isOnTrialPlan } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import getContactWithIntegrationTeamLink from "@app/util/integrations/getContactWithIntegrationTeamLink";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./ObjectSyncSettings.module.scss";

interface Props {
  entityType: EntityTypesSupportedByIntegrations;
  incoming: boolean;
  isTrial?: boolean;
  onChange?: (
    entityType: EntityTypesSupportedByIntegrations,
    incoming: boolean,
    outgoing: boolean
  ) => void;
  outgoing: boolean;
}

const ObjectSyncSettings: React.FC<Props> = ({
  entityType,
  incoming,
  isTrial,
  onChange,
  outgoing,
}) => {
  const intl = useIntl();

  const handleChangeEnabled = useCallback(
    (enabled: boolean) => onChange?.(entityType, enabled, false),
    [entityType, onChange]
  );

  const handleChangeIncoming = useCallback(
    (checked: boolean) => onChange?.(entityType, checked, outgoing),
    [entityType, onChange, outgoing]
  );

  const handleChangeOutgoing = useCallback(
    (checked: boolean) => onChange?.(entityType, incoming, checked),
    [entityType, incoming, onChange]
  );

  return (
    <div className={styles.entityRow}>
      <div>
        <Tag className={styles.tag} color="processing">
          {getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })}
        </Tag>
        {(incoming || outgoing) && (
          <>
            <div>
              <TextWithInfo
                info={
                  <div>
                    <div className={styles.tooltipTitle}>
                      <FormattedMessage
                        id="integrations.objectSyncSettings.incoming.tooltip.title"
                        defaultMessage="Incoming sync"
                        description="Text of incoming sync direction tooltip title"
                      />
                    </div>
                    <Divider />
                    <div>
                      <FormattedMessage
                        id="integrations.objectSyncSettings.incoming.tooltip.text"
                        defaultMessage="By default, integrations move records in one directionfrom your CRM into Map My Customers."
                        description="Text of incoming sync direction tooltip paragraph"
                      />
                    </div>
                  </div>
                }
              >
                <Checkbox
                  checked={incoming}
                  disabled={!localSettings.wasSkeletonKeyUsed()}
                  onChange={handleChangeIncoming}
                >
                  <FormattedMessage
                    id="integrations.objectSyncSettings.incoming"
                    defaultMessage="Incoming"
                    description="Name of incoming sync direction"
                  />
                </Checkbox>
              </TextWithInfo>
            </div>
            <div className={styles.outgoing}>
              <TextWithInfo
                info={
                  <div>
                    <div className={styles.tooltipTitle}>
                      <FormattedMessage
                        id="integrations.objectSyncSettings.outgoing.tooltip.title"
                        defaultMessage="Outgoing sync"
                        description="Text of outgoing sync direction tooltip title"
                      />
                    </div>
                    <Divider />
                    <div>
                      <FormattedMessage
                        id="integrations.objectSyncSettings.outgoing.tooltip.text"
                        defaultMessage="Only advanced integrations support outgoing or multi-directional syncs, in which records move from Map My Customers into a CRM (or both directions)"
                        description="Text of outgoing sync direction tooltip paragraph"
                      />
                    </div>
                    <ButtonLink
                      className={styles.tooltipAction}
                      href={getContactWithIntegrationTeamLink(isTrial ?? false)}
                    >
                      <FormattedMessage
                        id="integrations.objectSyncSettings.outgoing.tooltip.action"
                        defaultMessage="Inquire about advanced syncs"
                        description="Text of outgoing sync direction tooltip action"
                      />
                    </ButtonLink>
                  </div>
                }
              >
                <Checkbox
                  checked={outgoing}
                  disabled={!localSettings.wasSkeletonKeyUsed()}
                  onChange={handleChangeOutgoing}
                >
                  <FormattedMessage
                    id="integrations.objectSyncSettings.outgoing"
                    defaultMessage="Outgoing"
                    description="Name of Outgoing sync direction"
                  />
                </Checkbox>
              </TextWithInfo>
            </div>
          </>
        )}
      </div>
      <Switch checked={incoming || outgoing} onChange={handleChangeEnabled} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isTrial: isOnTrialPlan(state),
});

export default connect(mapStateToProps, null)(ObjectSyncSettings);
